import { useEffect, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Form, Modal, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatDateTime, formatDate } from '../utils/dateFormat';

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const when = (iso) => (iso ? formatDateTime(iso) : '-');
const LINK = '{link}';

/**
 * Sends a document on WhatsApp, or by email: the message (editable), with a link that opens it without logging in, and the number
 * from the customer's (or vendor's) record. {@code path} is the document under /api/share, such as "statements/12" (a
 * customer's statement of dues), "sale-bills/55" or "purchase-orders/40/vendors/5"; {@code stopPath} gives the URL that
 * stops a link when it isn't the usual one. The link is made when Send is pressed, and the links sent before are listed
 * with how often they were opened.
 */
function WhatsAppShareModal({ path, title, what = 'statement', party = 'customer', offerUpi = true, stopPath, onHide, options, days = 30 }) {
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [channel, setChannel] = useState('WHATSAPP');
  const [email, setEmail] = useState('');
  const [upi, setUpi] = useState(false);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!path) return;
    setLoading(true);
    Promise.all([
      axios.get(`/api/share/${path}/preview`, { params: options }),
      axios.get(`/api/share/${path}`)
    ])
      .then(([p, l]) => {
        setMessage(p.data.message || '');
        setPhone(p.data.whatsappPhone || '');
        setEmail(p.data.email || '');
        // No WhatsApp number but an email on the record: start on email
        if (!p.data.whatsappPhone && p.data.email) setChannel('EMAIL');
        setUpi(Boolean(p.data.upi));
        setLinks(l.data || []);
      })
      .catch((error) => toast.error(errorMessage(error, 'Could not prepare the message')))
      .finally(() => setLoading(false));
  }, [path, options]);

  const send = async () => {
    if (!message.includes(LINK)) {
      toast.warn(`Keep ${LINK} in the message: it becomes the link to the ${what}`);
      return;
    }
    // Opened now, while the click counts, so the browser doesn't block it; pointed at WhatsApp once the link exists
    const win = window.open('', '_blank');
    setSending(true);
    try {
      const { data } = await axios.post(`/api/share/${path}`, options || undefined);
      const text = message.split(LINK).join(data.url);
      const digits = phone.replace(/\D/g, '');
      const wa = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
      if (win) win.location.href = wa; else window.location.href = wa;
      toast.success('WhatsApp opened with the message. Press send there.');
      onHide();
    } catch (error) {
      if (win) win.close();
      toast.error(errorMessage(error, 'Could not create the link'));
    } finally {
      setSending(false);
    }
  };

  const sendEmail = async () => {
    if (!message.includes(LINK)) {
      toast.warn(`Keep ${LINK} in the message: it becomes the link to the ${what}`);
      return;
    }
    if (!email.trim()) { toast.warn('Enter the email address'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`/api/share/${path}/email`, { ...(options || {}), to: email.trim(), message });
      toast.success(`Emailed to ${data.to}`);
      onHide();
    } catch (error) {
      toast.error(errorMessage(error, 'Could not send the email'));
    } finally {
      setSending(false);
    }
  };

  const stop = async (link) => {
    try {
      await axios.post(stopPath ? stopPath(link) : `/api/share/${link.id}/revoke`);
      setLinks((all) => all.map((l) => (l.id === link.id ? { ...l, live: false, stopped: true } : l)));
      toast.success('Link stopped');
    } catch (error) {
      toast.error(errorMessage(error, 'Could not stop the link'));
    }
  };

  return (
    <Modal show={Boolean(path)} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5"><i className={`bi ${channel === 'EMAIL' ? 'bi-envelope' : 'bi-whatsapp text-success'} me-2`} />{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-3"><Spinner animation="border" /></div>
        ) : (
          <>
            <ButtonGroup size="sm" className="mb-3">
              <Button variant={channel === 'WHATSAPP' ? 'success' : 'outline-success'} onClick={() => setChannel('WHATSAPP')}>
                <i className="bi bi-whatsapp me-1" />WhatsApp
              </Button>
              <Button variant={channel === 'EMAIL' ? 'primary' : 'outline-primary'} onClick={() => setChannel('EMAIL')}>
                <i className="bi bi-envelope me-1" />Email
              </Button>
            </ButtonGroup>
            {channel === 'EMAIL' ? (
              <Form.Group className="mb-3">
                <Form.Label>Email to</Form.Label>
                <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={`accounts@${party}.com`} />
                <Form.Text className="text-muted">
                  {email ? `From the ${party}’s record.` : `Not on the ${party}’s record.`} It goes in your business&apos;s name; replies come to your organization&apos;s email.
                </Form.Text>
              </Form.Group>
            ) : (
            <Form.Group className="mb-3">
              <Form.Label>WhatsApp number</Form.Label>
              <Form.Control value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="With country code, e.g. 919876543210" />
              <Form.Text className="text-muted">
                {phone ? `From the ${party}’s record.` : `Not on the ${party}’s record: leave it empty to choose the contact in WhatsApp.`}
              </Form.Text>
            </Form.Group>
            )}
            <Form.Group className="mb-2">
              <Form.Label>Message</Form.Label>
              <Form.Control as="textarea" rows={7} value={message} onChange={(e) => setMessage(e.target.value)} />
              <Form.Text className="text-muted">
                {LINK} becomes a link to the {what}, as it stands on the day they open it, valid for {days} days. They don&apos;t need to log in.
              </Form.Text>
            </Form.Group>
            {offerUpi && !upi && (
              <Alert variant="info" className="small py-2 mt-3 mb-0">
                Add your UPI ID in Org Admin &rsaquo; Org settings to put a &ldquo;Pay by UPI&rdquo; button and QR code on the {what} for the amount due.
              </Alert>
            )}
            {links.length > 0 && (
              <>
                <h6 className="mt-4">Sent before</h6>
                <Table size="sm" bordered className="small mb-0">
                  <thead><tr><th>Sent</th><th>By</th><th>How</th><th>Opened</th><th>Link</th><th /></tr></thead>
                  <tbody>
                    {links.map((l) => (
                      <tr key={l.id}>
                        <td>{when(l.createdAt)}</td>
                        <td>{l.createdBy || '-'}</td>
                        <td>{l.channel === 'EMAIL' ? 'Email' : 'WhatsApp'}</td>
                        <td>{l.views > 0 ? `${l.views} time${l.views === 1 ? '' : 's'}, last ${when(l.lastViewedAt)}` : 'Not yet'}</td>
                        <td>
                          {l.live ? <Badge bg="success">Works until {formatDate(l.expiresAt)}</Badge>
                            : <Badge bg="secondary">{l.stopped ? 'Stopped' : 'Expired'}</Badge>}
                        </td>
                        <td className="text-end">
                          {l.live && <Button size="sm" variant="link" className="p-0" onClick={() => stop(l)}>Stop</Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        {channel === 'EMAIL' ? (
          <Button variant="primary" onClick={sendEmail} disabled={loading || sending}>
            <i className="bi bi-envelope me-1" />{sending ? 'Sending...' : 'Send email'}
          </Button>
        ) : (
          <Button variant="success" onClick={send} disabled={loading || sending}>
            <i className="bi bi-whatsapp me-1" />{sending ? 'Opening...' : 'Send on WhatsApp'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default WhatsAppShareModal;
